import type { Logger } from "@expert/logging";
import type { KyInstance } from "ky";

const ADHERENCE_THRESHOLD = 0;

interface ChecklistSummaryRow {
    behavior: string;
    isInvalidData: boolean;
    isWarning: boolean;
    percent: number;
}

export interface ChecklistSummaryStatsResponse {
    totalEligibleSessions: number;
    checklistSummary: { behavior: string; count: number; title: string }[];
}

export interface ChecklistSummaryStats {
    checklistSummaryRows: Array<ChecklistSummaryRow>;
    sessionCount: number;
    overallAdherencePercentDisplay: string;
}

export function processChecklistSummaryData(
    stats: ChecklistSummaryStatsResponse,
    adherenceThreshold: number = ADHERENCE_THRESHOLD,
): ChecklistSummaryStats {
    const sessionCount = stats?.totalEligibleSessions || 0;
    const checklistSummaryRows = (stats?.checklistSummary || []).map(({ title, count }) => {
        const percent = Math.round((count / sessionCount) * 100);

        const isInvalidData = sessionCount === 0;
        const isWarning = percent <= adherenceThreshold;

        return {
            behavior: title,
            isInvalidData,
            isWarning,
            percent,
        };
    });
    const overallAdherencePercent =
        checklistSummaryRows.reduce((total, { percent }) => total + percent, 0) / checklistSummaryRows.length;
    const overallAdherencePercentDisplay =
        Number.isNaN(overallAdherencePercent) || sessionCount === 0 ? "N/A" : `${Math.round(overallAdherencePercent)}%`;

    return {
        checklistSummaryRows,
        sessionCount,
        overallAdherencePercentDisplay,
    };
}

export async function getChecklistSummaryStats(
    client: KyInstance,
    logger: Logger,
    start: string,
    end: string,
): Promise<ChecklistSummaryStats | undefined> {
    try {
        logger.info({ start, end }, "Fetching checklist summary stats");
        const params = new URLSearchParams({ start, end });

        const result = await client.get(`v1/metrics/checklist-summary?${params}`).json<ChecklistSummaryStatsResponse>();

        return processChecklistSummaryData(result);
    } catch (error) {
        logger.warn({ error }, "Error fetching checklist summary stats");
    }
}
